import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@mui/icons-material/Close";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";
import { DatePicker } from "antd";
import CategoryContext from "context/category-context";
import { StoreContext } from "context/store";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";

import DropZone from "components/UpStyled/DropZone";
import { seasonOptions } from "utils/data";
import api from "services/api";

import moment from "moment";

const STYLE_DESC_MAX_LENGTH = 2000;
const MAX_STYLE_NUMBER_COUNT = 12;
const MAX_TAG_LENGTH = 40;

const CreateCollectionModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      isEdit = false,
      collection = {},
      isloading = false,
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
    },
    ref
  ) => {
    const [moodboard, setMoodboard] = useState([]);
    const [collectionDesc, setCollectionDesc] = useState("");
    const [collectionTags, setCollectionTags] = useState([]);
    const [inputTag, setInputTag] = useState("");
    const [uploading, setUploading] = useState(false);
    const {
      tree,
      actions: { findNodeByID },
    } = useContext(CategoryContext);

    const { store } = useContext(StoreContext);

    useImperativeHandle(
      ref,
      () => ({
        moodboard,
        collectionTags,
        inputTag,
        setMoodboard,
        setCollectionTags,
      }),
      [moodboard, collectionTags, inputTag]
    );

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      reset,
      control,
      formState: { errors },
    } = useForm();

    useEffect(() => {
      if (isEdit) {
        const meta = collection?.metadata ? collection.metadata : {};
        setMoodboard(meta?.moodboard || []);

        reset({
          collection_name: collection?.title,
          year: meta?.year ? moment(meta?.year) : null,
          season: meta?.season,
          description: collection?.description,
          brand: meta?.brand,
        });
        setCollectionDesc(collection?.description);
        //setCollectionTags(meta?.tags && typeof meta.tags == "object" ? meta?.tags : []);
      } else {
        //when create new collection, prefill brand info with designer's business name

        const business_name = store?.name;
        reset({ brand: business_name });
      }
    }, [isEdit]);

    const handleDelete = (i) => {
      //setCollectionTags(collectionTags.filter((tag, index) => index !== i));
    };

    const handleAddition = (tag) => {
      if (!tag.id) {
        return;
      }
      const findTag = collectionTags.find((x) => x.id == tag.id);

      if (findTag && findTag.text) {
        return toast.error(`Tag: ${findTag?.text} already in the list!`);
      }
      if (tag?.text.length >= MAX_TAG_LENGTH) {
        return toast.error(`Your tag is longer than the maximum allowed limit:${MAX_TAG_LENGTH}!`);
      }
      if (collectionTags.length >= MAX_STYLE_NUMBER_COUNT) {
        return toast.error(`You can have at most ${MAX_STYLE_NUMBER_COUNT} tags!`);
      }
      setCollectionTags([...collectionTags, tag]);
      setInputTag("");
    };

    const handleDrag = (tag, currPos, newPos) => {
      const newTags = collectionTags.slice();

      newTags.splice(currPos, 1);
      newTags.splice(newPos, 0, tag);

      // re-render
      setCollectionTags(newTags);
    };

    const KeyCodes = {
      comma: 188,
      enter: 13,
    };

    const delimiters = [KeyCodes.comma, KeyCodes.enter];

    const handleInputChange = (value) => {
      setInputTag(value);
    };

    const handleInputBlur = (value) => {
      handleAddition({ id: value, text: value });
    };

    const handleCreateCollection = async (data) => {
      let newCreatedCollection = null;
      try {
        setUploading(true);
        const moodboardFiles = await handleUploadFiles(moodboard);
        if (!isEdit) {
          //create new collection
          let payload = {
            title: data["collection_name"],
            description: data["description"],
            metadata: {
              year: data["year"],
              season: data["season"],
              //tags: collectionTags,
              brand: data["brand"],
              moodboard: moodboardFiles,
            },
          };

          newCreatedCollection = await api.collections.create(payload);
        } else {
          //edit collection
          let payload = {
            title: data["collection_name"],
            description: data["description"],
            metadata: {
              year: data["year"],
              season: data["season"],
              //tags: collectionTags,
              brand: data["brand"],
              moodboard: moodboardFiles,
            },
          };

          await api.collections.update(collection.id, payload);
        }
        setUploading(false);
        handleClose();

        toast.success(
          `Your Collection:${data["collection_name"]} has been successfully ${
            isEdit ? "edited" : "created"
          }!`
        );
        handleAfterOk(newCreatedCollection);

        setTimeout(() => {
          handleRefresh();
        }, refreshDelay);
      } catch (err) {
        console.log(err);
        setUploading(false);
        if (err?.response?.status == 401) {
          return toast.error(err?.response?.data?.error || err?.response?.statusText);
        }
        toast.error(`Your Collection could not be ${isEdit ? "edited" : "created"} !`);
      }
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title">
          {isEdit ? "Edit Collection" : "Create New Collection"}
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleOk ? handleOk : handleCreateCollection)}
          style={{ overflow: "auto", maxHeight: "800px" }}
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        required
                        textTransform="capitalize"
                      >
                        Collection Name
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput
                      placeholder="Collection Name"
                      required
                      {...register("collection_name", {
                        required: "A collection name is required!",
                      })}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Brand
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput placeholder="Brand" {...register("brand")} />
                  </SuiBox>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Year
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="year"
                      control={control}
                      render={({ field }) => {
                        return (
                          <SuiBox>
                            <DatePicker
                              {...field}
                              picker="year"
                              placeholder="Year"
                              style={{ width: "100%" }}
                              size="large"
                              popupStyle={{
                                maxHeight: 400,

                                overflow: "auto",
                                zIndex: 9999,
                              }}
                            />
                          </SuiBox>
                        );
                      }}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Season
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="season"
                      control={control}
                      render={({ field }) => {
                        return (
                          <SuiBox>
                            <SuiSelect
                              {...field}
                              options={seasonOptions}
                              placeholder="Season"
                              backspaceRemovesValue
                              isClearable
                              isCreatable
                            />{" "}
                          </SuiBox>
                        );
                      }}
                    />
                  </SuiBox>
                </Grid>
                {/* <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                        info={`Using "," or press enter to input multiple colors.`}
                      >
                        Tags
                      </SuiTypography>
                    </SuiBox>

                    <SuiTagInput
                      placeholder="tags"
                      size="small"
                      tags={collectionTags}
                      delimiters={delimiters}
                      handleDelete={handleDelete}
                      handleAddition={handleAddition}
                      handleDrag={handleDrag}
                      inputFieldPosition="bottom"
                      name="tags"
                      autocomplete
                      handleInputChange={handleInputChange}
                      maxLength={MAX_TAG_LENGTH}
                      autofocus={false}
                      handleInputBlur={handleInputBlur}
                    />
                  </SuiBox>
                </Grid> */}
                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography component="label" variant="caption" fontWeight="bold">
                        Description
                      </SuiTypography>
                    </SuiBox>

                    <SuiInput
                      multiline
                      rows="4"
                      componentsProps={{ input: { maxlength: STYLE_DESC_MAX_LENGTH } }}
                      {...register("description", {
                        maxLength: {
                          value: STYLE_DESC_MAX_LENGTH,
                          message: `Maxmium length should not exceed ${STYLE_DESC_MAX_LENGTH} characters`,
                        },
                        value: collectionDesc,
                        onChange: (e) => {
                          setCollectionDesc(e.target.value);
                        },
                      })}
                      placeholder="Your style description..."
                    />

                    <SuiBox display="flex" justifyContent="space-between">
                      <div>
                        <ErrorMessage
                          errors={errors}
                          name="description"
                          render={({ message }) => (
                            <SuiTypography
                              variant="text"
                              fontWeight="regular"
                              fontSize="12px"
                              color="error"
                              className="ml-2"
                            >
                              {message}
                            </SuiTypography>
                          )}
                        />
                      </div>
                      <SuiTypography
                        variant="text"
                        color="secondary"
                        fontWeight="regular"
                        fontSize="14px"
                      >
                        {collectionDesc ? collectionDesc.length : 0}/{STYLE_DESC_MAX_LENGTH}
                      </SuiTypography>
                    </SuiBox>
                  </SuiBox>
                </Grid>

                {/* <Grid item xs={12} sm={12}>
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Moodboard
                      </SuiTypography>
                    </SuiBox>
                    <DropZone
                      files={moodboard}
                      setfiles={setMoodboard}
                      options={{ accept: { "image/*": [".jpeg", ".png"] } }}
                    />
                  </Grid> */}
              </Grid>
              <SuiBox
                pt={3}
                px={3}
                display="flex"
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton
                      variant="gradient"
                      size="medium"
                      onClick={handleClose}
                      className="mr-2"
                      color="light"
                    >
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton
                      size="medium"
                      variant="gradient"
                      color="dark"
                      type="submit"
                      disabled={isloading}
                    >
                      {isEdit ? "Save" : "Create"}
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </form>
      </Dialog>
    );
  }
);

export default CreateCollectionModal;
